"use client";

import React from "react";
import { Clock, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Link from "next/link";
import { useTimeEntryHooks } from "@/lib/hooks/api/time-entries/time-entry-hooks";
import { LinkedEntityContextShell } from "./LinkedEntityContextShell";
import {
  getLinkedEntityFullPageHref,
  getTaskLinkedEntityId,
  normalizeTaskLinkedEntityType,
} from "@/lib/utils/crm/task-linked-entity";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Contact_Tasks } from "@/src/gql/graphql";

/**
 * Linked time entry
 */
export function TaskLinkedTimeEntryPanel({ item }: TabContentProps<Contact_Tasks>) {
  const entryId = getTaskLinkedEntityId(item);
  const enabled = normalizeTaskLinkedEntityType(item.entity_type) === "time_entries" && !!entryId;
  const timeEntryHooks = useTimeEntryHooks();
  const { data: entry, isLoading, error } = timeEntryHooks.useSingle(entryId ?? "", { enabled });

  if (!enabled) return null;

  return (
    <LinkedEntityContextShell task={item} expectedKind="time_entries">
      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Loading time entry…</p>
          </CardContent>
        </Card>
      ) : error || !entry ? (
        <Card>
          <CardContent className="py-8 text-center text-red-600 dark:text-red-400">
            <p>Could not load this time entry.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="w-full space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-card-foreground">Time entry</h3>
            </div>
            <Button asChild variant="outline" size="sm" className="gap-2">
              <Link href={getLinkedEntityFullPageHref("time_entries", entry.id)}>
                <ExternalLink className="w-4 h-4" />
                Open time entry
              </Link>
            </Button>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{(entry as { description?: string }).description || "Time entry"}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              {(entry as { hours?: number }).hours != null ? (
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Hours: </span>
                  <span>{(entry as { hours?: number }).hours}</span>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}
    </LinkedEntityContextShell>
  );
}
